import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import player from 'assets/images/player.png';
import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import Text, { TextProps } from 'components/atoms/Text';
import mapModifiers from 'utils/functions';

type DescriptionStyle = {
  desc: TextProps['modifiers'];
  ratio: Ratio
}

interface VideoCardProps {
  description: string;
  imageBackground: string;
  handleClick?: () => void;
  isLive?: boolean;
  isSmaller?: boolean;
  isVertical?: boolean;
  date?: string;
}

const VideoCard: React.FC<VideoCardProps> = ({
  description,
  handleClick,
  imageBackground,
  isLive,
  isSmaller,
  isVertical,
  date,
}) => {
  const { t } = useTranslation();
  const { ratio, desc } = useMemo((): DescriptionStyle => {
    if (isSmaller) return { ratio: '161x92', desc: ['700', 'raisinBlack', 'letterSpacing'] };
    if (isVertical) return { ratio: '273x156', desc: ['700', 'md', 'raisinBlack'] };
    if (date) return { ratio: '853x479', desc: ['700', 'white'] };
    return { ratio: '558x314', desc: ['700', 'white', 'xl', 'letterSpacing'] };
  }, [isVertical, isSmaller, date]);

  return (
    <div className={mapModifiers('o-videoCard', isSmaller && 'smaller', isVertical && 'vertical')}>
      <div
        className={mapModifiers('o-videoCard_wrapImage', isSmaller && 'smaller')}
      >
        <div
          className={mapModifiers(
            'o-videoCard_background',
            isSmaller && 'smaller',
          )}
        >
          <Image
            imgSrc={imageBackground}
            ratio={ratio}
            alt="background"
            size="cover"
          />
        </div>
        <div className="o-videoCard_layer" />
        {isLive && (
          <div className="o-videoCard_live">
            <div className="o-videoCard_icon">
              <Icon size="32" iconName="live" />
            </div>
            <div className="o-videoCard_liveText">
              <Text modifiers={['700', 'white']}>{t('additional.live_streaming')}</Text>
            </div>
          </div>
        )}
        <div
          className={mapModifiers(
            'o-videoCard_buttonPlayer',
            isSmaller && 'smaller',
          )}
          onClick={handleClick}
        >
          <Image alt="player" ratio="1x1" size="cover" imgSrc={player} />
        </div>
      </div>
      <div className={mapModifiers('o-videoCard_wrapInfo', date && !isSmaller && !isVertical && 'insideImage')}>
        <div
          className={mapModifiers(
            'o-videoCard_description',
            isSmaller && 'smaller',
          )}
          onClick={handleClick}
        >
          <Text
            modifiers={desc}
          >
            {description}
          </Text>
        </div>
        {
          !isSmaller && date && (
          <div className="o-videoCard_time">
            <div className="o-videoCard_iconTime">
              <Icon iconName={isVertical ? 'time' : 'timeWhite'} size={date && !isVertical ? '24' : '16'} />
            </div>
            <Text
              modifiers={['500', isVertical ? 'sm-small' : 'default', isVertical ? 'davysGrey' : 'white']}
            >
              {date}
            </Text>
          </div>
          )
        }
      </div>
    </div>
  );
};

VideoCard.defaultProps = {
  handleClick: undefined,
  isLive: false,
  isSmaller: false,
  isVertical: false,
  date: undefined,
};

export default VideoCard;
